import {
	Component,
	DestroyRef,
	ElementRef,
	InputSignal,
	OutputEmitterRef,
	Signal,
	ViewChild,
	WritableSignal,
	computed,
	effect,
	inject,
	input,
	output,
	signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatLabel } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, Subject, lastValueFrom, of, Subscription } from 'rxjs';
import { catchError, finalize, switchMap } from 'rxjs/operators';

// models
import { IChat, IChatsResponse } from '../all-planogram-chats/chat';
import { Filter, FilterItem } from '../filters-panel/models/filter';

// services
import { ChatService } from '../../services/chat.service';
import { SnackBarService } from '../../services/snack-bar.service';
import { FiltersPanelService } from '../filters-panel/filters-panel.service';
import { FiltersService } from '../filters-panel/filters.service';

// components
import { PhIconComponent } from '../ph-icon/ph-icon.component';
import { PhIconWrapperComponent } from '../ph-icon-wrapper/ph-icon-wrapper.component';
import { SearchComponent } from '../search/search.component';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import {
	CHAT_SKELETON_DATE_THEME,
	CHAT_SKELETON_IMAGE_THEME,
	CHAT_SKELETON_ITEMS,
	CHAT_SKELETON_ITEMS_INITIAL,
	CHAT_SKELETON_LINE_SHORT_THEME,
	CHAT_SKELETON_LINE_THEME
} from '../all-planogram-chats/chat-skeleton.config';

// pipes
import { MentionParserPipe } from '../../pipes/mention-parser.pipe';
import { HumanDatePipe } from '../../pipes/human-date.pipe';

@Component({
	selector: 'app-add-planogram-chat-drawer',
	standalone: true,
	templateUrl: './add-planogram-chat-drawer.component.html',
	styleUrls: ['./add-planogram-chat-drawer.component.scss'],
	imports: [
		CommonModule,
		MatSidenavModule,
		MatTooltipModule,
		TranslateModule,
		PhIconComponent,
		PhIconWrapperComponent,
		SearchComponent,
		MentionParserPipe,
		HumanDatePipe,
		MatLabel,
		InfiniteScrollDirective,
		FaIconComponent,
		NgxSkeletonLoaderModule
	]
})
export class AddPlanogramChatDrawerComponent {
	opened: InputSignal<boolean> = input<boolean>(false);
	close: OutputEmitterRef<void> = output<void>();
	chatAdded: OutputEmitterRef<void> = output<void>();

	_opened: WritableSignal<boolean> = signal<boolean>(false);
	searchQuery: WritableSignal<string> = signal<string>('');
	selectedPlanIds: WritableSignal<Array<number>> = signal<Array<number>>([]);
	chats: WritableSignal<Array<IChat>> = signal<Array<IChat>>([]);
	loading: WritableSignal<boolean> = signal<boolean>(false);
	addingIds: WritableSignal<Set<number>> = signal<Set<number>>(new Set<number>());
	nextUrl: WritableSignal<string | null> = signal<string | null>(null);
	loadingNext: WritableSignal<boolean> = signal<boolean>(false);
	filtersApplied: WritableSignal<boolean> = signal<boolean>(false);
	readonly skeletonItemsInitial: number[] = CHAT_SKELETON_ITEMS_INITIAL;
	readonly skeletonItems: number[] = CHAT_SKELETON_ITEMS;
	readonly skeletonImageTheme = CHAT_SKELETON_IMAGE_THEME;
	readonly skeletonLineTheme = CHAT_SKELETON_LINE_THEME;
	readonly skeletonLineShortTheme = CHAT_SKELETON_LINE_SHORT_THEME;
	readonly skeletonDateTheme = CHAT_SKELETON_DATE_THEME;

	addingIdsSet: Signal<Set<number>> = computed((): Set<number> => this.addingIds());

	private filterUpdates$: Subject<void> = new Subject<void>();
	private filters: Array<Filter> = [];
	private destroyRef: DestroyRef = inject(DestroyRef);
	private applySubscription?: Subscription;

	@ViewChild('chatListContainer') chatListContainer!: ElementRef<HTMLDivElement>;

	constructor(
		private chatService: ChatService,
		private snackBarService: SnackBarService,
		private filtersPanelService: FiltersPanelService,
		private filtersService: FiltersService
	) {
		effect((): void => {
			if (this.opened()) {
				this._opened.set(true);
				this.filterUpdates$.next();
			} else {
				this._opened.set(false);
			}
		});

		this.filterUpdates$
			.pipe(
				switchMap((): Observable<IChatsResponse> => {
					this.loading.set(true);
					this.chats.set([]);
					this.nextUrl.set(null);

					const params: Record<string, string | number> = {
						type: 'PLANOGRAM'
					};

					const search: string = this.searchQuery();
					if (search) params['search'] = search;

					const planIds: Array<number> = this.selectedPlanIds();
					if (planIds?.length) params['plan_id'] = planIds.join(',');

					return this.chatService.getAvailableChats(params).pipe(
						catchError((): Observable<IChatsResponse> => {
							this.snackBarService.setErrorNotification();
							return of({ results: [], next: null, previous: null, count: 0, has_data: false });
						}),
						finalize((): void => this.loading.set(false))
					);
				}),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe((response: IChatsResponse): void => {
				const results: Array<IChat> = response?.results ?? [];
				const uniqueById: Map<number, IChat> = new Map<number, IChat>();
				results.forEach((chat: IChat): void => {
					uniqueById.set(chat.id, chat);
				});
				this.chats.set(Array.from(uniqueById.values()));
				this.nextUrl.set(response?.next ?? null);
				setTimeout((): void => this.loadUntilScrollable());
			});
	}

	onClose(): void {
		this._opened.set(false);
		this.searchQuery.set('');
		this.selectedPlanIds.set([]);
		this.filtersApplied.set(false);
		this.filters = [];
		this.filtersPanelService.setFilters([]);
		this.filtersPanelService.setApplyPanel(false);
		this.filtersPanelService.setShowPanel(false);
		this.close.emit();
	}

	onSearch(value: string): void {
		this.searchQuery.set(value ?? '');
		this.filterUpdates$.next();
	}

	clearFilters(): void {
		if (this.filters.length) {
			this.filtersPanelService.setApplyPanel(false);
		} else {
			this.selectedPlanIds.set([]);
			this.filtersApplied.set(false);
			this.filterUpdates$.next();
		}
	}

	openFiltersPanel(): void {
		this.filtersPanelService.setShowPanel(true);

		if (!this.filters.length) {
			this.filtersPanelService.setFilters([]);
			void this.setFilersPanel();
		} else {
			this.filtersPanelService.setFilters(this.filters);
		}
	}

	private async setFilersPanel(): Promise<void> {
		const originalPlans: Array<FilterItem> = await lastValueFrom(this.filtersService.getPlans());
		if (!originalPlans.length) {
			this.filters = [];
			this.filtersPanelService.setFilters([]);
			return;
		}

		this.filters = [this.filtersService.getStorePlansFilter(1, originalPlans)];

		this.filtersPanelService.setFilters(this.filters);

		if (this.applySubscription) {
			this.applySubscription.unsubscribe();
		}

		this.applySubscription = this.filtersPanelService.getApplyPanel()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((apply: boolean): void => {
				if (!this._opened()) return;

				const plansFilter: Filter | undefined = this.filters.find((filter: Filter): boolean => filter.param === 'plans');
				if (!plansFilter) return;

				if (apply) {
					const selectedIds: Array<number> = plansFilter.selectingStrategy.getSelected() as Array<number>;
					const planIds: Array<number> = selectedIds ?? [];

					this.selectedPlanIds.set(planIds);
					this.filtersApplied.set(planIds.length > 0);
				} else {
					this.selectedPlanIds.set([]);
					this.filtersApplied.set(false);
				}

				this.filterUpdates$.next();
			});
	}

	loadNextChats(): void {
		const next: string = this.nextUrl();
		if (!next || this.loadingNext()) return;
		this.loadingNext.set(true);
		this.chatService.loadNextChats(next)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (response: IChatsResponse): void => {
					if (response?.results?.length > 0) {
						this.chats.update((current: Array<IChat>): Array<IChat> => [...current, ...response.results]);
						this.nextUrl.set(response.next);
					} else {
						this.nextUrl.set(null);
					}
					this.loadingNext.set(false);
					setTimeout((): void => this.loadUntilScrollable());
				},
				error: (): void => {
					this.loadingNext.set(false);
					this.snackBarService.setErrorNotification();
				}
			});
	}

	private loadUntilScrollable(): void {
		const container: HTMLDivElement = this.chatListContainer?.nativeElement;
		if (!container || !this.nextUrl() || this.loadingNext() || this.loading()) return;
		if (container.scrollHeight > container.clientHeight) return;
		this.loadNextChats();
	}

	onAddChat(chat: IChat): void {
		if (this.addingIds().has(chat.id)) return;
		this.addingIds.update((set: Set<number>): Set<number> => new Set([...set, chat.id]));
		const search: string = this.searchQuery().trim();
		const planIds: Array<number> = this.selectedPlanIds();

		const params: {
			type: string;
			plan_id?: string;
			search?: string;
			ids: Array<number>;
		} = {
			type: 'PLANOGRAM',
			ids: [chat.id]
		};

		if (planIds?.length) params.plan_id = planIds.join(',');
		if (search) params.search = search;

		this.chatService.joinChats(params).pipe(
			catchError((): Observable<void> => {
				this.snackBarService.setErrorNotification();
				return of(undefined);
			}),
			finalize((): void => {
				this.addingIds.update((set: Set<number>): Set<number> => {
					const next = new Set(set);
					next.delete(chat.id);
					return next;
				});
			})
		).subscribe((): void => {
			this.chats.update((list: Array<IChat>): Array<IChat> => list.filter((c: IChat): boolean => c.id !== chat.id));
			this.chatAdded.emit();
		});
	}

}
